import { Injectable } from '@angular/core';
import { FirebaseService } from './firebase.service';
import { ModuleEnum } from '../_utils/unums/module.enum';
import { Storage } from '@ionic/storage-angular';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private readonly baseUrl: string;
  private readonly settingsBaseUrl: string;

  constructor(
    private firebaseService: FirebaseService,
    private storage: Storage,
    private http: HttpClient,
  ) {
    this.baseUrl = `${environment.apiUrl}/modules`;
    this.settingsBaseUrl = `${environment.apiUrl}/settings`;
  }

  public async getModuleSettings(module: ModuleEnum | string): Promise<any> {
    const settings = await this.getCompanyDefaultSettings(module);
    if (settings) {
      return settings;
    }
    return await lastValueFrom(this.getModuleByKey(module));
  }

  public getModuleByKey = (key: string): Observable<any> => this.http.get<any>(`${this.baseUrl}/key/${key}`);

  public getSettingById = async (id: string): Promise<any> =>
    await lastValueFrom(this.http.get<any>(`${this.settingsBaseUrl}/${encodeURIComponent(id)}`));

  public getSettingByCategoryAndKey = (category: string, key: string): Observable<any> =>
    this.http.get<any>(this.settingsBaseUrl, { params: { category, key } });

  public async getMenuSettings(menuSettings: any[], companyId: string): Promise<any> {
    const menu = [...menuSettings];
    if (!companyId) {
      return menu;
    }
    const companyMenu = await this.firebaseService.getDocument(`companies/${companyId}/settings/menu`);
    if (!companyMenu) {
      return menu;
    }
    return menu.map(menuModule => {
      if (!menuModule?.items) {
        return menuModule;
      }
      menuModule.items = menuModule.items.map((item: any) => {
        item.hidden = !!companyMenu[menuModule.module]?.items?.[item?.key]?.hidden;
        return item;
      });
      return menuModule;
    });
  }

  public async getHeaderSettings(): Promise<any> {
    return await this.getCompanySettings(`header`);
  }

  public async getCompanyAssessments(key: string): Promise<any> {
    return await this.getCompanySettings(`assessments/${key}`);
  }

  public async getContentCompany(key: string): Promise<any> {
    return await this.getCompanySettings(`content/${key}`);
  }

  public getCompanyTutors = async (): Promise<string[]> => {
    const user = await this.storage.get('user');
    return await this.firebaseService.getDocument(`companies/${user.companyId}/settings/tutors`);
  };

  public getForumManagers = async (): Promise<string[]> => await this.firebaseService.getDocument(`settings/forumManagers`);

  public getDefaultRedirectPath = async (key: string): Promise<string> => {
    const module = await lastValueFrom(this.getModuleByKey(key));
    return module.defaultRedirectPath;
  };

  private async getCompanyDefaultSettings(module: ModuleEnum | string): Promise<any> {
    return await this.getCompanySettings(`modules/${module}`);
  }

  private async getCompanySettings(path: string): Promise<any> {
    const user = await this.storage.get('user');
    return await this.firebaseService.getDocument(`companies/${user.companyId}/settings/${path}`);
  }

}
